import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Card component (shadcn/ui style)
 *
 * A versatile container component for grouping related content.
 * Supports header, title, description, content, and footer sections.
 *
 * Institutional Console redesign (issue #234, DESIGN-SPEC.md §2.1/§2.2/§4.2/§8):
 * plain title/description header (no icon-chip — that pattern lives, if at
 * all, in individual consumer components, never here) and a shadow that is
 * OFF in dark themes and a subtle 1px shadow in `console-light` only, via the
 * `shadow-card` Tailwind class bound to the `--shadow-card` token (see
 * tailwind.config.js `boxShadow.card` and styles/themes/console-*.css).
 * Legacy themes fall back to the pre-existing static `--shadow-sm` value so
 * their appearance is unchanged.
 *
 * @example
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Card Title</CardTitle>
 *     <CardDescription>Card description text</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     Main content goes here
 *   </CardContent>
 *   <CardFooter>
 *     Footer actions
 *   </CardFooter>
 * </Card>
 */

const Card = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border border-border bg-card text-card-foreground shadow-card",
      // Improved border visibility in dark mode
      "dark:border-border/70 [data-theme='dark']:border-[#3d444d] [data-theme='navy']:border-[#3a4d6b] [data-theme='brownie']:border-[#4a3f35] [data-theme='ai-fantasy']:border-[#4a3d5c]",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1 px-3 pt-3 pb-1", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

// False positive: CardTitle is a polymorphic wrapper whose `children` arrive
// via the `{...props}` spread below, which the static jsx-a11y rule can't
// trace back to this definition site. Every call site in the codebase passes
// real heading content (verified: no `<CardTitle />` self-closing usages
// exist).
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-3 pt-0 pb-3", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center px-4 pt-0 pb-4", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
